/**
 * Service for managing college contacts (per college on athlete profile).
 */

import { buildApiUrl } from '../utils/apiConfig';
import { jsonSend } from '../utils/http';
import { CreateCollegeContactRequest, CollegeContactResponse } from '../types/collegeContacts';

export const fetchCollegeContacts = async (
    athleteId: number,
    collegeId: number
): Promise<CollegeContactResponse[]> => {
    const url = buildApiUrl(`/api/athletes/${athleteId}/colleges/${collegeId}/contacts`);

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (response.status === 401) {
        window.location.href = '/login';
        throw new Error('Unauthorized');
    }

    if (response.status === 404) {
        throw new Error('Athlete not found');
    }

    if (!response.ok) {
        throw new Error('Failed to fetch contacts');
    }

    return await response.json();
};

export const createCollegeContact = async (
    athleteId: number,
    collegeId: number,
    payload: CreateCollegeContactRequest
): Promise<CollegeContactResponse> => {
    const url = buildApiUrl(`/api/athletes/${athleteId}/colleges/${collegeId}/contacts`);
    return await jsonSend<CollegeContactResponse>(url, 'POST', payload, true);
};

export const deleteCollegeContact = async (athleteId: number, contactId: number): Promise<void> => {
    const url = buildApiUrl(`/api/athletes/${athleteId}/college/contacts/${contactId}`);

    const response = await fetch(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (response.status === 401) {
        window.location.href = '/login';
        throw new Error('Unauthorized');
    }

    if (response.status === 404) {
        throw new Error('Contact not found or does not belong to this athlete');
    }

    if (!response.ok) {
        throw new Error('Failed to delete contact');
    }
};

export const updateCollegeContact = async (
    athleteId: number,
    contactId: number,
    payload: CreateCollegeContactRequest
): Promise<CollegeContactResponse> => {
    const url = buildApiUrl(`/api/athletes/${athleteId}/college/contacts/${contactId}`);
    return await jsonSend<CollegeContactResponse>(url, 'PUT', payload, true);
};
